import React from 'react';
import '../App.css';
import { getCardLoc, getCardTypeName, getCardTypeClass, getUILoc } from '../utils/cardUtils.js';
import audioManager from '../utils/AudioManager.js';

/**
 * カードイラスト共通コンポーネント
 * modeに合わせてコンテナクラスを切り替えます
 */
export const CardIllustration = ({ cardId, isBackground = false, isMini = false, className = '' }) => {
  const [imageError, setImageError] = React.useState(false);

  if (imageError || !cardId) return null;

  const path = `${process.env.PUBLIC_URL}/assets/cards/c${cardId}.png`;

  // クラス名の決定
  const containerClass = isBackground
    ? "card-bg-illustration-container"
    : isMini
      ? "related-card-illustration-mini"
      : "card-center";

  const imgClass = isBackground ? "card-bg-illustration" : "card-illustration";

  return (
    <div className={`${containerClass} ${className}`}>
      <img
        src={path}
        alt=""
        className={imgClass}
        onError={() => setImageError(true)}
      />
    </div>
  );
};

/**
 * イデオロギーカードの「稼働中」を示す月の満ち欠けアニメーション
 */
const MoonSpinner = () => {
  const [phase, setPhase] = React.useState(0);
  const phases = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setPhase((prev) => (prev + 1) % phases.length);
    }, 200); // 200ms ごとに切り替え
    return () => clearInterval(timer);
  }, [phases.length]);

  return <span style={{ fontSize: '0.9em', filter: 'drop-shadow(0 0 2px rgba(173, 255, 47, 0.5))' }}>{phases[phase]}</span>;
};

const Card = ({ card, mode = 'library', onClick, onAnimationEnd, id, location, isInsufficientScale }) => {
  // 翻訳データの取得
  const cardLoc = getCardLoc(card.id);
  const cardName = cardLoc.name;
  const cardDescription = cardLoc.description;
  const cardTypeName = getCardTypeName(card.card_type);
  const cardTypeClass = getCardTypeClass(card.card_type);

  // modeに応じてベースとなるクラスを決定
  const baseClassName = mode === 'game' ? 'card-game' : 'card-library';

  // カード名の長さに応じてクラスを追加（CSSでの微調整用）
  const nameLengthClass = cardName.length > 10 ? 'name-long' : cardName.length > 6 ? 'name-medium' : 'name-short';

  // 規模不足クラスの追加
  const insufficientScaleClass = isInsufficientScale ? 'card-insufficient-scale' : '';

  const cardClassName = `${baseClassName} ${onClick ? 'clickable' : ''} card-type-${cardTypeClass} ${card.animation || ''} ${nameLengthClass} ${insufficientScaleClass}`;

  const handleClick = () => {
    if (onClick) {
      audioManager.playSE('select');
      onClick(card);
    }
  };

  const handleAnimationEnd = () => {
    if (card.animation === 'destroying' && onAnimationEnd) {
      onAnimationEnd(card.instance_id);
    }
  };

  // 1: 財カード, 2: イベントカード, 3: イデオロギーカード
  const isWealth = card.card_type === 1;
  const isIdeology = card.card_type === 3;
  const durabilityValue = card.current_durability !== undefined ? card.current_durability : card.durability;

  // デフォルトIDの生成（場所情報があれば付加する）
  const defaultId = location ? `card_${card.id}_${location}` : `card_${card.id}`;

  // ゲームモード用のシンプルなカード表示
  if (mode === 'game') {
    return (
      <div 
        className={cardClassName} 
        onClick={handleClick}
        onAnimationEnd={handleAnimationEnd}
        data-card-id={card.instance_id}
        id={id || defaultId}
      >
        <div className="card-header">
          <div className="card-name-container">
            <div className="card-name">{cardName}</div>
          </div>
        </div>
        
        {/* 共通イラストコンポーネントを使用 */}
        <CardIllustration cardId={card.id} />

        <div className="card-footer">
          <div className="card-cost">
            {location !== 'field' && <span>⭐ {card.required_scale}</span>}
            {isWealth && (
              <span className="card-durability-value">
                {location !== 'field' && ' / '}🛡️ {durabilityValue}
              </span>
            )}
            {isIdeology && location === 'field' && (
              <span className="card-ideology-active">
                <MoonSpinner />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ライブラリ/デッキ構築モード用の詳細なカード表示
  return (
    <div 
      className={cardClassName} 
      onClick={handleClick}
      onAnimationEnd={handleAnimationEnd}
      data-card-id={card.instance_id}
      id={id}
    >
      {/* 背景イラスト（ライブラリモード用） */}
      <CardIllustration cardId={card.id} isBackground={true} />

      <div className="card-header">
        <div className="card-header-main">
          <h3>{cardName}</h3>
          {card.is_token && <span className="token-badge">TOKEN</span>}
        </div>
        <span className="card-type">{cardTypeName}</span>
      </div>

      <div className="card-info">
        <div>{getUILoc('LABELS.REQUIRED_SCALE')}: {card.required_scale}</div>
        {isWealth && (
          <div>{getUILoc('LABELS.DURABILITY')}: {card.durability}</div>
        )}
      </div>
      <div className="card-description">
        {cardDescription}
      </div>
    </div>
  );
};

export default Card;
